import React from "react";
import styles from "./ReportStepFrame.module.scss";

export interface ReportStepFrameProps {
  /** The step's own content, and the only thing a step supplies. */
  children: React.ReactNode;
  /**
   * The step's own controls, in the corner `ReportShell` puts the back and
   * forward pair. The last step ends the report rather than continuing it, so
   * its buttons belong to its content and fade with it, and the position
   * stays defined here rather than being re-derived by a step that would
   * then drift from its neighbours.
   */
  actions?: React.ReactNode;
  /**
   * The step's own content in the foot-left corner, where `ReportShell`
   * otherwise puts the score readout. The last step asks for feedback there
   * instead of repeating a number the learner has already seen five times.
   */
  footer?: React.ReactNode;
  /**
   * The step is arriving with something carried over from the one before
   * it, so it was swapped in without the report's usual fade. The furniture
   * here fades in on its own instead of appearing at full strength at once.
   */
  arriving?: boolean;
  /**
   * Sets the content in the middle of the frame. Every other step hangs its
   * title a fixed distance under the pips, as the web frames draw it.
   */
  centred?: boolean;
}

/**
 * The furniture every report step shares, inside `ReportShell`.
 *
 * Steps pass content and nothing else. The score readout and the back and
 * forward controls are the shell's, so they hold still across a step change
 * and no step can move them a few pixels relative to its neighbours.
 */
const ReportStepFrame: React.FC<ReportStepFrameProps> = ({
  children,
  actions,
  footer,
  arriving = false,
  centred = false,
}) => (
  <div
    className={styles.step}
    data-testid="report-step-frame"
    data-arriving={arriving}
    data-centred={centred}
    data-has-foot={Boolean(footer || actions)}
  >
    <div className={styles.content}>{children}</div>

    {(footer || actions) && (
      <div className={styles.foot}>
        {footer && <div className={styles.footer}>{footer}</div>}
        {actions && <div className={styles.controls}>{actions}</div>}
      </div>
    )}
  </div>
);

export default ReportStepFrame;
